import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { ChartsReferenceLine } from '@mui/x-charts/ChartsReferenceLine';
import { LineChart } from '@mui/x-charts/LineChart';
import type { ReactNode } from 'react';
import {
  TREND_CHART_SERIES_COLORS,
  TrendChartAreaGradient,
  trendChartSx,
} from '../../../media/breakdown/TrendChart.js';
import { ErrorState } from '../../../media/shared/ErrorState.js';
import { NoData } from '../../../media/shared/NoData.js';
import { formatMetricValue } from '../../data/format.js';
import type { CompletionCurveOutput, CreativeDetailScope } from '../data/api.js';
import { useCompletionCurve } from '../data/api.js';
import { ChartSection } from '../shared/ChartSection.js';
import { SectionSkeleton } from '../shared/SectionSkeleton.js';
import { ChartLegend } from './ChartLegend.js';

const MILESTONES = [
  { percent: 25, label: '25% view rate', tier: 'high' },
  { percent: 50, label: '50% view rate', tier: 'medium' },
  { percent: 75, label: '75% view rate', tier: 'low' },
] as const;

const CURVE_SERIES_ID = 'viewers';

const MAX_TICKS = 6;

interface DropOffSectionProps {
  scope: CreativeDetailScope;
  creativeId: string;
}

function DropOffTiles({ curve }: { curve: CompletionCurveOutput }): ReactNode {
  const tiles = [
    { label: 'Total impressions', value: formatMetricValue(curve.impressions, 'number', '') },
    { label: 'Completion', value: formatMetricValue(curve.completionRate, 'percentage', '') },
  ];

  return (
    <Box sx={{ display: 'flex', gap: 10, mb: 4 }}>
      {tiles.map((tile) => (
        <Box key={tile.label}>
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 600,
              textTransform: 'uppercase',
              color: 'text.secondary',
            }}
          >
            {tile.label}
          </Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.primary', mt: 1 }}>
            {tile.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function DropOffChart({ curve }: { curve: CompletionCurveOutput }): ReactNode {
  const theme = useTheme();
  if (curve.curve.length === 0) return <NoData message="No drop-off data for this creative." />;

  const inSeconds = curve.durationSeconds !== null;
  const points = [{ percent: 0, seconds: 0, rate: 1 }, ...curve.curve];
  const xOf = (point: (typeof points)[number]): number =>
    inSeconds ? (point.seconds ?? 0) : point.percent;
  const formatX = (value: number): string =>
    inSeconds ? `${Number(value.toFixed(2))} sec` : `${Number(value.toFixed(2))}%`;
  const formatRate = (value: number | null): string =>
    value === null ? '-' : `${Math.round(value)}%`;

  const milestones = MILESTONES.flatMap((milestone) => {
    const index = points.findIndex((point) => point.percent === milestone.percent);
    const point = points[index];
    if (point === undefined) return [];
    return [
      { ...milestone, index, x: xOf(point), color: theme.palette.score[milestone.tier].main },
    ];
  });

  return (
    <>
      <LineChart
        height={280}
        margin={{ top: 10, bottom: 10, left: 8, right: 32 }}
        grid={{ horizontal: true }}
        axisHighlight={{ x: 'line' }}
        xAxis={[
          {
            data: points.map(xOf),
            scaleType: 'linear',
            min: 0,
            tickNumber: MAX_TICKS,
            valueFormatter: (value: number) => formatX(value),
          },
        ]}
        yAxis={[{ min: 0, max: 100, width: 50, valueFormatter: (value: number) => `${value}%` }]}
        series={[
          {
            id: CURVE_SERIES_ID,
            label: 'Viewers',
            data: points.map((point) => point.rate * 100),
            color: TREND_CHART_SERIES_COLORS[0],
            curve: 'linear',
            area: true,
            showMark: false,
            valueFormatter: formatRate,
          },
          ...milestones.map((milestone) => ({
            id: milestone.label,
            label: milestone.label,
            data: points.map((point, index) =>
              index === milestone.index ? point.rate * 100 : null,
            ),
            color: milestone.color,
            shape: 'circle' as const,
            showMark: true,
            valueFormatter: () => null,
          })),
        ]}
        hideLegend
        sx={trendChartSx(theme)}
      >
        <TrendChartAreaGradient />
        {milestones.map((milestone) => (
          <ChartsReferenceLine
            key={milestone.label}
            x={milestone.x}
            lineStyle={{ stroke: milestone.color, strokeDasharray: '3 3' }}
          />
        ))}
      </LineChart>
      <ChartLegend
        entries={milestones.map((milestone) => ({
          id: milestone.label,
          label: milestone.label,
          color: milestone.color,
        }))}
      />
    </>
  );
}

export function DropOffSection({ scope, creativeId }: DropOffSectionProps): ReactNode {
  const curve = useCompletionCurve({ ...scope, creativeId }, true);

  return (
    <ChartSection variant="card" title="Drop-Off (Viewer% vs. Time)">
      {curve.isError ? (
        <ErrorState />
      ) : curve.data === undefined ? (
        <SectionSkeleton variant="chart" />
      ) : (
        <>
          <DropOffTiles curve={curve.data} />
          <DropOffChart curve={curve.data} />
        </>
      )}
    </ChartSection>
  );
}
